import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  MapPin,
  CurrencyEur,
  CalendarBlank,
  File,
  Plus,
  Users,
  CheckSquare,
  Trash,
  PencilSimple,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { ROLE_PROJET, SPECIALITES_PARTENAIRE, libellePartenaire, FONCTIONS, etapeChantier, libelleEtape, ETAPES_CHANTIER } from '../../lib/libelles';
import ReunionSemaine from './ReunionSemaine';  // RP28-31
import PlanifierReunionDialog from './PlanifierReunionDialog';  // A2
import SyntheseDossier, { FriseEtapes } from './SyntheseDossier';  // B1
import AjouterAuChantierDialog, { FONCTIONS_EQUIPE } from './AjouterAuChantierDialog';  // B1 (B16)
import usePermissions from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { projectRights } from './projectRights';
import MemberPermissionsPanel from './MemberPermissionsPanel';
import InvitationLinkDialog from '../shared/InvitationLinkDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// B1 — la Synthèse du dossier est pour toute l'équipe (cabinet, partenaires, AMO) ; le client
// et l'entreprise ont leur propre écran (« Où en est-on », « Cette semaine »).
const ROLES_SYNTHESE = ['archi', 'moe_principal', 'moe_membre', 'consultant', 'amo'];

/**
 * Vue d'ensemble du chantier.
 * B1 : la « Synthèse du dossier » remplace le grand rectangle photo (P1#15) ; l'étape est
 * dite en tête ; une seule liste « Équipe du chantier » (client compris, créateur d'office)
 * et une seule fenêtre « Ajouter quelqu'un au chantier » (B16).
 */
export default function ProjectOverview({ chantier, onUpdate, onNavigateTab }) {
  const { perms, role_projet } = usePermissions(chantier.id);
  const { user } = useAuth();
  // RP01 — n'afficher que ce que le serveur accepte.
  const rights = projectRights({ userRole: user?.role, roleProjet: role_projet, perms });
  // RP01 — le client n'a pas le bloc réunions (compteur et « Prochaines réunions »).
  const isClient = rights.isClient;
  const voitSynthese = !isClient && ROLES_SYNTHESE.includes(role_projet);
  const [documents, setDocuments] = useState([]);
  // A2 (B8, constat 21) — réunions planifiées (evenements), pas les comptes rendus
  const [reunions, setReunions] = useState([]);
  const [planifierOpen, setPlanifierOpen] = useState(false);
  const [reunionSemaine, setReunionSemaine] = useState(false);  // RP28-31
  const [nbIntervenants, setNbIntervenants] = useState(null);
  const [dejaAjoutes, setDejaAjoutes] = useState(new Set());
  const [ajout, setAjout] = useState(null);  // mode de la fenêtre « Ajouter quelqu'un »
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetchData();
  }, [chantier.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const results = await Promise.all([
        axios.get(`${API_URL}/api/documents/${chantier.id}`, { withCredentials: true }).catch(() => ({ data: [] })),
        isClient
          ? Promise.resolve({ data: [] })
          : axios.get(`${API_URL}/api/chantiers/${chantier.id}/evenements`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setDocuments(Array.isArray(results[0].data) ? results[0].data : []);
      setReunions(Array.isArray(results[1].data) ? results[1].data : []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const rafraichir = () => { setRefreshKey((k) => k + 1); fetchData(); };

  // « Pour bien démarrer » : chaque ligne ouvre directement la bonne action
  const onAction = (action) => {
    if (action === 'equipe') setAjout('cabinet');
    else if (action === 'client') setAjout('client');
    else if (action === 'reunion') {
      if (rights.canPlanMeeting) setPlanifierOpen(true); else onNavigateTab && onNavigateTab('suivi');
    }
  };

  const debutJour = new Date(); debutJour.setHours(0, 0, 0, 0);
  const reunionsAVenir = reunions
    .filter((r) => r.date_debut && new Date(r.date_debut) >= debutJour)
    .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));
  const upcomingMeetings = reunionsAVenir.slice(0, 3);
  const etape = etapeChantier(chantier);

  return (
    <div className="space-y-6">
      {/* B1 — Où en est le dossier */}
      {voitSynthese ? (
        <SyntheseDossier
          chantierId={chantier.id}
          onNavigateTab={onNavigateTab}
          onAction={perms.can_modify_team || rights.canPlanMeeting ? onAction : undefined}
          refreshKey={refreshKey}
          onChanged={onUpdate}
        />
      ) : (
        <div className="card-kroquis" data-testid="overview-etape-publique">
          <p className="text-xs uppercase tracking-wider text-[#6B6B6B]">Où en est-on</p>
          <p className="text-lg text-[#2C2C2C] mt-1" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Étape actuelle : {libelleEtape(etape, isClient)}
          </p>
          <div className="mt-3">
            <FriseEtapes compact frise={ETAPES_CHANTIER
              .filter((e) => e.cle !== 'cloture' || etape === 'cloture')
              .map((e, i, arr) => {
                const rang = arr.findIndex((x) => x.cle === etape);
                return { cle: e.cle, libelle: isClient ? e.public : e.libelle, etat: i < rang ? 'fait' : (i === rang ? 'en_cours' : 'a_venir') };
              })} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Fiche du chantier — P1#15 : plus de photo d'illustration ; P5#2 : le client est dans l'équipe */}
          <div className="card-kroquis" data-testid="overview-fiche">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <MapPin size={20} className="text-[#C75B39] mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-1">Adresse</p>
                  <p className="font-medium text-[#2C2C2C] break-words">{chantier.address}</p>
                </div>
              </div>
              {chantier.budget ? (
                <div className="flex items-start gap-3">
                  <CurrencyEur size={20} className="text-[#C75B39] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-1">Budget travaux</p>
                    <p className="font-medium text-[#2C2C2C]">{Number(chantier.budget).toLocaleString('fr-FR')} € HT</p>
                  </div>
                </div>
              ) : null}
              <div className="flex items-start gap-3">
                <CalendarBlank size={20} className="text-[#C75B39] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-1">Dates</p>
                  <p className="font-medium text-[#2C2C2C]">
                    {chantier.start_date
                      ? new Date(chantier.start_date).toLocaleDateString('fr-FR')
                      : 'Non définies'}
                    {chantier.end_date && ` → ${new Date(chantier.end_date).toLocaleDateString('fr-FR')}`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Équipe du chantier (client compris) */}
          {!isClient && (
            <EquipeProjetSection
              chantierId={chantier.id}
              canModify={perms.can_modify_team}
              onAjouter={(mode) => setAjout(mode || 'cabinet')}
              refreshKey={refreshKey}
              onEquipe={(d) => { setNbIntervenants(d?.nb_intervenants ?? 0); setDejaAjoutes(new Set((d?.membres || []).map((m) => m.user_id).filter(Boolean))); }}
              onChanged={rafraichir}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="card-kroquis">
            <h3 className="text-base font-semibold text-[#2C2C2C] mb-4">Résumé</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-[#E0E0DB]">
                <span className="text-sm text-[#6B6B6B] flex items-center gap-2">
                  <File size={16} /> Documents
                </span>
                <span className="font-semibold text-[#2C2C2C]">{documents.length}</span>
              </div>
              {!isClient && (
              <div className="flex items-center justify-between py-2 border-b border-[#E0E0DB]" data-testid="overview-stat-reunions">
                <span className="text-sm text-[#6B6B6B] flex items-center gap-2">
                  <CalendarBlank size={16} /> Réunions
                </span>
                <span className="font-semibold text-[#2C2C2C]" title="Réunions à venir">{reunionsAVenir.length}</span>
              </div>
              )}
              {!isClient && (
              <div className="flex items-center justify-between py-2" data-testid="overview-stat-intervenants">
                <span className="text-sm text-[#6B6B6B] flex items-center gap-2">
                  <Users size={16} /> Intervenants
                </span>
                {/* P1#4 — compteur = personnes de la liste « Équipe du chantier » */}
                <span className="font-semibold text-[#2C2C2C]">{nbIntervenants ?? '—'}</span>
              </div>
              )}
            </div>
          </div>

          {/* Upcoming Meetings — RP01 : masqué au client (réunions refusées par le serveur) */}
          {!isClient && (
          <div className="card-kroquis" data-testid="overview-upcoming-meetings">
            <h3 className="text-base font-semibold text-[#2C2C2C] mb-4">Prochaines réunions</h3>
            {/* RP28-31 (ARC-A1) — accès direct à l'écran de la réunion hebdomadaire */}
            {rights.canPlanMeeting && (
              <Button
                className="w-full mb-3 bg-[#1E3A5F] hover:bg-[#2C4F7D] rounded-none gap-2 min-h-[44px]"
                onClick={() => setReunionSemaine(true)}
                data-testid="overview-reunion-semaine-btn"
              >
                <Users size={16} /> {FONCTIONS.reunionSemaine}
              </Button>
            )}
            {reunionSemaine && (
              <ReunionSemaine chantierId={chantier.id} onClose={() => { setReunionSemaine(false); rafraichir(); }} />
            )}
            {/* A2 (constat 4) — le raccourci « Planifier une réunion » ouvre directement le formulaire */}
            {rights.canPlanMeeting && (
              <PlanifierReunionDialog
                chantierId={chantier.id}
                open={planifierOpen}
                onClose={() => setPlanifierOpen(false)}
                onCreated={() => rafraichir()}
              />
            )}
            {upcomingMeetings.length === 0 ? (
              <p className="text-sm text-[#6B6B6B] text-center py-4">Aucune réunion prévue</p>
            ) : (
              <div className="space-y-3">
                {upcomingMeetings.map((reunion) => (
                  <div key={reunion.id} className="flex items-center gap-3 p-3 bg-[#F5F5F3] border border-[#E0E0DB]" data-testid={`overview-reunion-${reunion.id}`}>
                    <div className="w-10 text-center">
                      <p className="text-lg font-bold text-[#C75B39]">
                        {new Date(reunion.date_debut).getDate()}
                      </p>
                      <p className="text-xs text-[#6B6B6B] uppercase">
                        {new Date(reunion.date_debut).toLocaleDateString('fr-FR', { month: 'short' })}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{reunion.titre || 'Réunion de chantier'}</p>
                      <p className="text-xs text-[#6B6B6B] truncate">
                        {new Date(reunion.date_debut).toLocaleDateString('fr-FR', { weekday: 'long' })} à {new Date(reunion.date_debut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        {reunion.lieu ? ` — ${reunion.lieu}` : ''}
                      </p>
                      <p className="text-[11px] text-[#6B6B6B]">
                        {reunion.convocation_envoyee ? 'Convoquée' : 'Pas encore convoquée'}
                        {(reunion.invites || []).length > 0 ? ` · ${(reunion.invites || []).length} participant${(reunion.invites || []).length > 1 ? 's' : ''}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Quick Actions (Iter63b : conditionné can_see_quick_actions) */}
          {perms.can_see_quick_actions && (
          <div className="card-kroquis" data-testid="quick-actions-section">
            <h3 className="text-base font-semibold text-[#2C2C2C] mb-4">Actions rapides</h3>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start gap-2 rounded-none text-sm"
                onClick={() => onNavigateTab && onNavigateTab('documents')} data-testid="quick-action-add-document">
                <File size={16} /> Ajouter un document
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2 rounded-none text-sm"
                onClick={() => (rights.canPlanMeeting ? setPlanifierOpen(true) : onNavigateTab && onNavigateTab('planning'))}
                data-testid="quick-action-plan-meeting">
                <CalendarBlank size={16} /> Planifier une réunion
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2 rounded-none text-sm"
                onClick={() => onNavigateTab && onNavigateTab('suivi')} data-testid="quick-action-manage-intervenants">
                <Users size={16} /> Suivi chantier
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2 rounded-none text-sm"
                onClick={() => onNavigateTab && onNavigateTab('taches')} data-testid="quick-action-create-task">
                <CheckSquare size={16} /> Créer une action
              </Button>
            </div>
          </div>
          )}
        </div>
      </div>

      {perms.can_modify_team && (
        <AjouterAuChantierDialog
          open={!!ajout}
          modeInitial={ajout || 'cabinet'}
          onClose={() => setAjout(null)}
          chantierId={chantier.id}
          prospection={chantier.status === 'prospection'}
          dejaAjoutes={dejaAjoutes}
          onAdded={() => { rafraichir(); onUpdate?.(); }}
        />
      )}
    </div>
  );
}


// Iter63a-bis / RP04 — libellés du rôle projet (09-VOCABULAIRE §4).
const ROLE_PROJET_LABEL = ROLE_PROJET;

/**
 * B1 — « Équipe du chantier » : une seule liste (membres, créateur, client), lue sur
 * GET /api/chantiers/{id}/equipe-complete. Le client y figure avec son accès (P1#5, P5#2).
 */
function EquipeProjetSection({ chantierId, canModify = true, onAjouter, refreshKey = 0, onEquipe, onChanged }) {
  const [equipe, setEquipe] = useState({ membres: [], clients: [], nb_intervenants: 0 });
  const [editMember, setEditMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lienInvitation, setLienInvitation] = useState(null);

  const loadEquipe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/equipe-complete`, { withCredentials: true });
      setEquipe(data || { membres: [], clients: [], nb_intervenants: 0 });
      onEquipe?.(data);
    } catch { /* */ } finally { setLoading(false); }
  }, [chantierId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadEquipe(); }, [loadEquipe, refreshKey]);

  const updateMember = async () => {
    if (!editMember) return;
    try {
      await axios.put(`${API_URL}/api/projets/${chantierId}/moe/${editMember.id}`, {
        role: editMember.role, mission: editMember.mission,
      }, { withCredentials: true });
      // RP38 / D6 — spécialité du partenaire (OPC : planning ; économiste : budget sur autorisation)
      if (editMember.role_projet === 'consultant' && (editMember.specialite || null) !== (editMember._specialiteInitiale || null)) {
        await axios.put(`${API_URL}/api/chantiers/${chantierId}/partenaires/${editMember.id}/specialite`,
          { specialite: editMember.specialite || null }, { withCredentials: true });
      }
      toast.success('Mis à jour');
      setEditMember(null);
      loadEquipe();
    } catch (e) { toast.error('Modification impossible'); }
  };

  const removeMember = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/projets/${chantierId}/moe/${id}`, { withCredentials: true });
      toast.success('Retiré de l\'équipe');
      loadEquipe();
      onChanged?.();
    } catch (e) { toast.error('Retrait impossible'); }
  };

  // B14 — l'accès du client est explicite : inviter / retirer l'accès
  const inviterClient = async (c) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/clients/inviter`, { email: c.email }, { withCredentials: true });
      if (data?.email_sent) toast.success(`Invitation envoyée à ${c.email}`);
      else if (data?.activation_url) {
        toast.warning("Accès ouvert, mais l'e-mail n'est pas parti");
        setLienInvitation({ ...data, user_name: c.nom, user_email: c.email });
      } else toast.warning(`${c.email} a accès au chantier. L'e-mail n'est pas parti : prévenez-le qu'il se connecte avec son adresse habituelle.`);
      loadEquipe();
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Invitation impossible');
    }
  };
  const retirerAccesClient = async (c) => {
    if (!window.confirm(`Retirer l'accès de ${c.nom} au chantier ?`)) return;
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/clients/retirer-acces`, { email: c.email }, { withCredentials: true });
      toast.success('Accès retiré');
      loadEquipe();
      onChanged?.();
    } catch { toast.error('Impossible de retirer l\'accès'); }
  };

  const vide = equipe.membres.length === 0 && equipe.clients.length === 0;

  return (
    <div className="card-kroquis" data-testid="equipe-projet-section">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="text-lg font-semibold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
          Équipe du chantier
        </h3>
        {canModify && (
          <Button variant="outline" size="sm" className="rounded-none gap-1 text-xs" onClick={() => onAjouter?.('cabinet')} data-testid="add-equipe-btn">
            <Plus size={14} /> Ajouter quelqu'un
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-[#9A9A9A]">Chargement…</p>
      ) : vide ? (
        <p className="text-sm text-[#9A9A9A] text-center py-4">Personne pour l'instant. Ajoutez l'équipe et le client.</p>
      ) : (
        <div className="space-y-2">
          {equipe.membres.map(m => {
            // RP38 — « Partenaire — BET structure » ; RP40 — « AMO »
            const roleProjetLabel = m.role_projet === 'consultant' ? libellePartenaire(m.specialite)
              : (m.role_projet ? ROLE_PROJET_LABEL[m.role_projet] : null);
            return (
            <div key={m.id} className="flex items-center gap-3 p-2 bg-[#F5F5F3] border border-[#E0E0DB]" data-testid={`equipe-member-${m.id}`}>
              <div className="w-8 h-8 bg-[#1E3A5F] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                {m.name?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#2C2C2C] truncate">
                  {m.name}
                  {roleProjetLabel && (
                    <span className="ml-2 inline-block text-[10px] uppercase tracking-wide bg-[#E0E0DB] text-[#2C2C2C] px-1.5 py-0.5" data-testid={`equipe-role-projet-${m.id}`}>
                      {roleProjetLabel}
                    </span>
                  )}
                  {m.createur && <span className="ml-1 text-[10px] text-[#6B6B6B]">(a créé le chantier)</span>}
                  {m.sans_acces && <span className="ml-1 text-[10px] text-[#9A9A9A] border border-[#E0E0DB] px-1">sans accès</span>}
                </p>
                <p className="text-xs text-[#6B6B6B] truncate">
                  {m.role}{m.mission ? ` — ${m.mission}` : ''}
                  {m.email ? ` • ${m.email}` : ''}
                  {m.telephone ? ` • ${m.telephone}` : ''}
                </p>
              </div>
              {canModify && m.type === 'membre' && (
                <>
                  <button onClick={() => setEditMember({ ...m, _specialiteInitiale: m.specialite || null })} className="text-[#6B6B6B] hover:text-[#2C2C2C]" title="Modifier" data-testid={`equipe-edit-${m.id}`}>
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={() => removeMember(m.id)} className="text-[#6B6B6B] hover:text-[#8B3A3A]" title="Retirer" data-testid={`equipe-remove-${m.id}`}>
                    <Trash size={14} />
                  </button>
                </>
              )}
            </div>
            );
          })}
          {equipe.clients.map((c, i) => (
            <div key={`client-${i}`} className="flex items-center gap-3 p-2 bg-[#FFF8F3] border border-[#E0E0DB]" data-testid={`equipe-client-${i}`}>
              <div className="w-8 h-8 bg-[#C75B39] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                {c.nom?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#2C2C2C] truncate">
                  {c.nom}
                  <span className="ml-2 inline-block text-[10px] uppercase tracking-wide bg-[#E0E0DB] text-[#2C2C2C] px-1.5 py-0.5">Client</span>
                  <span className={`ml-1 text-[10px] px-1 border ${c.acces ? 'text-[#2D6A4F] border-[#C8E6C9]' : 'text-[#9A9A9A] border-[#E0E0DB]'}`} data-testid={`equipe-client-acces-${i}`}>
                    {c.acces ? 'a accès au chantier' : 'sans accès'}
                  </span>
                </p>
                <p className="text-xs text-[#6B6B6B] truncate">
                  {c.fonction}{c.email ? ` • ${c.email}` : ''}{c.telephone ? ` • ${c.telephone}` : ''}
                </p>
              </div>
              {canModify && c.email && !c.acces && (
                <Button size="sm" variant="outline" className="rounded-none text-xs gap-1" onClick={() => inviterClient(c)} data-testid={`equipe-client-inviter-${i}`}>
                  <EnvelopeSimple size={12} /> Inviter
                </Button>
              )}
              {canModify && c.email && c.acces && (
                <button onClick={() => retirerAccesClient(c)} className="text-xs text-[#6B6B6B] hover:text-[#8B3A3A]" data-testid={`equipe-client-retirer-${i}`}>
                  Retirer l'accès
                </button>
              )}
            </div>
          ))}
          {canModify && equipe.clients.length === 0 && (
            <button onClick={() => onAjouter?.('client')} className="text-xs text-[#C75B39] hover:underline flex items-center gap-1" data-testid="equipe-ajouter-client">
              <Plus size={12} /> Ajouter le client
            </button>
          )}
        </div>
      )}

      <InvitationLinkDialog invitation={lienInvitation} onClose={() => setLienInvitation(null)} />

      {/* Edit member dialog */}
      {editMember && (
        <Dialog open={!!editMember} onOpenChange={() => setEditMember(null)}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Modifier — {editMember.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Fonction</Label>
                <Select value={editMember.role} onValueChange={v => setEditMember({ ...editMember, role: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(FONCTIONS_EQUIPE.includes(editMember.role) ? FONCTIONS_EQUIPE : [editMember.role, ...FONCTIONS_EQUIPE]).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Mission</Label>
                <Input value={editMember.mission || ''} onChange={e => setEditMember({ ...editMember, mission: e.target.value })} className="rounded-none mt-1" placeholder="Ex : structure béton" />
              </div>
              {editMember.role_projet === 'consultant' && (
                <div>
                  <Label className="text-xs">Spécialité du partenaire</Label>
                  <Select value={editMember.specialite || 'aucune'} onValueChange={v => setEditMember({ ...editMember, specialite: v === 'aucune' ? null : v })}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="equipe-edit-specialite"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aucune">Non précisée</SelectItem>
                      {Object.entries(SPECIALITES_PARTENAIRE).map(([code, lib]) => <SelectItem key={code} value={code}>{lib}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#6B6B6B] mt-1">
                    L'OPC peut modifier le planning. L'économiste peut recevoir l'accès au budget (droits du membre).
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-b border-[#E5E5E5] pb-3">
                <Button variant="outline" onClick={() => setEditMember(null)} className="rounded-none">Annuler</Button>
                <Button onClick={updateMember} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">Enregistrer</Button>
              </div>
              {editMember.user_id ? (
                <div className="space-y-2">
                  {/* RP37 (D4) — les droits se règlent une fois par personne au niveau du
                      cabinet (Équipe MOE › Droits de l'équipe) ; le réglage fin de CE projet
                      reste accessible ici, replié sous « Avancé », et l'emporte. */}
                  <details className="border border-[#E5E5E5]" data-testid="member-perms-avance">
                    <summary className="cursor-pointer px-3 py-2 text-sm text-[#2C2C2C]">
                      Avancé — réglage fin des droits sur ce projet
                    </summary>
                    <div className="px-3 pb-3">
                      <p className="text-xs text-[#6B6B6B] mb-2">
                        Les droits de chaque personne se règlent pour tous les projets dans Équipe MOE › Droits de l'équipe.
                        Un réglage ici ne vaut que pour ce projet et l'emporte.
                      </p>
                      <MemberPermissionsPanel
                        chantierId={chantierId}
                        userId={editMember.user_id}
                        memberName={editMember.name}
                      />
                    </div>
                  </details>
                  <p className="text-xs text-[#9A9A9A]" data-testid="member-historique-deplace">
                    Historique des changements de droits : Équipe MOE › Droits de l'équipe › Historique.
                  </p>
                </div>
              ) : (
                <div className="text-xs text-[#6B6B6B] italic">
                  Les droits individuels ne concernent que les personnes qui ont un compte.
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
